import {
  Location,
  LocationCreateUpdateData,
  OperatingHoursTimespan,
  OperatingTimespansForDay,
} from 'features/locations/location.types';
import { API } from 'api';
import {
  formatPhoneNumber,
  maybeTransformEmptyRichTextContentToNull,
} from 'shared/utils';
import { getAbsoluteUrl } from 'shared/getAbsoluteUrl';
import { WEEK_HOURS_DAYS } from 'features/locations/location.constants';
import { SubmittedLocationData } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import {
  buildReporterNotesPayload,
  reporterNotesChangesToFormValues,
  reporterNotesDefaultFormValues,
} from 'features/reporterNotes/reporterNotes.utils';
import { Link } from 'shared/types';
import { AxiosResponse } from 'axios';
import { LocationFormValues } from './LocationForm.types';
import { GeoEventTypes, LinkTypes, LocationTypes } from '../../../../constants';

export const getSubmitButtonKey = (
  isEditing: boolean,
  isProvisional: boolean,
): string => {
  if (isProvisional) {
    return 'createEditLocation.buttons.createProvisional';
  }

  return isEditing
    ? 'createEditLocation.buttons.edit'
    : 'createEditLocation.buttons.create';
};

export const postLocation = ({
  data,
  skipRedundancyCheck,
}: {
  data: LocationCreateUpdateData;
  skipRedundancyCheck?: boolean;
}): Promise<AxiosResponse<Location>> =>
  API.post(`geo_events/`, {
    ...data,
    ...(skipRedundancyCheck && { skipRedundancyCheck }),
  });

export const updateLocation = (data: {
  id: number;
  data: LocationCreateUpdateData;
}): Promise<AxiosResponse<Location>> =>
  API.put(`geo_events/${data.id}`, data.data);

const formatLinksForForm = (links?: Link[]): Link[] =>
  links?.map((link) => ({
    ...link,
    ...(link.linkType === 'phone'
      ? { value: formatPhoneNumber(link.value) }
      : {}),
  })) ?? [];

const expandHoursForForm = (
  hours?: OperatingTimespansForDay[],
): OperatingHoursTimespan[] =>
  WEEK_HOURS_DAYS.map((dayOfWeek) => {
    const dayHours = hours?.find((hour) => hour.dayOfWeek === dayOfWeek);
    return {
      dayOfWeek,
      times: dayHours?.times || [],
      checked: Boolean(dayHours),
    };
  });

// Seeds the form from a provisional create submission under review.
export const convertProvisionalSubmissionDataToFormValues = (
  submittedData: SubmittedLocationData,
): LocationFormValues => ({
  name: submittedData.name || '',
  address: submittedData.address || '',
  type: submittedData.data.locationType || LocationTypes.evacShelter,
  parentGeoEvents: submittedData.parentGeoEvents,
  status: submittedData.isActive ? 'active' : 'inactive',
  information: submittedData.data.information || '',
  expandLinks: Boolean(submittedData.data.links?.length),
  links: formatLinksForForm(submittedData.data.links),
  lat: submittedData.lat || 0,
  lng: submittedData.lng || 0,
  regions: submittedData.regions ?? [],
  expandHours: Boolean(submittedData.data.hours?.length),
  hours: expandHoursForForm(submittedData.data.hours),
  atCapacity: Boolean(submittedData.data.atCapacity),
  reporterOnlyNotes: submittedData.data.reporterOnlyNotes || '',
  ...reporterNotesChangesToFormValues(submittedData.reporterNotes),
});

export const getInitialValues = (input: {
  location?: Location;
  latitude?: number;
  longitude?: number;
  reviewSubmittedData?: SubmittedLocationData;
}): LocationFormValues => {
  const { location, latitude, longitude, reviewSubmittedData } = input;
  if (reviewSubmittedData) {
    return convertProvisionalSubmissionDataToFormValues(reviewSubmittedData);
  }
  return {
    name: location?.name || '',
    address: location?.address || '',
    type: location?.data.locationType || LocationTypes.evacShelter,
    parentGeoEvents: location?.parentGeoEvents || [],
    // eslint-disable-next-line no-nested-ternary
    status: location ? (location.isActive ? 'active' : 'inactive') : 'active',
    information: location?.data.information || '',
    expandLinks: Boolean(location?.data?.links.length),
    links: formatLinksForForm(location?.data.links),
    lat: location?.lat || latitude || 0,
    lng: location?.lng || longitude || 0,
    regions: location?.regions ?? [],
    expandHours: Boolean(location?.data?.hours?.length),
    hours: expandHoursForForm(location?.data?.hours),
    atCapacity: Boolean(location?.data.atCapacity),
    reporterOnlyNotes: location?.data.reporterOnlyNotes || '',
    ...reporterNotesDefaultFormValues,
  };
};

export const locationFormDataToServerData = (
  values: LocationFormValues,
): LocationCreateUpdateData | null => {
  const {
    status,
    type,
    information,
    links,
    parentGeoEvents,
    hours,
    expandLinks,
    expandHours,
    atCapacity,
    reporterOnlyNotes,
    newReporterNote,
    reporterNoteToggles,
    ...rest
  } = values;
  if (!type) return null;
  return {
    ...rest,
    reporterNotes: buildReporterNotesPayload({
      newReporterNote,
      reporterNoteToggles,
    }),
    isActive: status === 'active',
    geoEventType: GeoEventTypes.location,
    parentGeoEvents: parentGeoEvents.map((parentGeoEvent) => ({
      id: parentGeoEvent.id,
    })),
    data: {
      geoEventType: GeoEventTypes.location,
      locationType: type,
      information,
      links: links.map((link) => {
        if (link.linkType === LinkTypes.website) {
          return { ...link, value: getAbsoluteUrl(link.value) };
        }
        return link;
      }),
      hours: hours
        .filter((hour) => {
          const hasCompleteTimestamps =
            hour.times.length > 0 &&
            hour.times.every(
              ({ openTime, closeTime }) =>
                typeof openTime === 'number' && typeof closeTime === 'number',
            );
          return hasCompleteTimestamps;
        })
        .map(({ dayOfWeek, times }) => ({
          dayOfWeek,
          times,
        })) as OperatingTimespansForDay[],
      atCapacity,
      reporterOnlyNotes:
        maybeTransformEmptyRichTextContentToNull(reporterOnlyNotes),
      reporterOnlyNotesDateModified: null,
    },
  };
};
